import React from 'react';
import { graphql } from '../../../../generated';
import { useModal } from '../../../../components/Modal/PresentedModal';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { FormForm } from '../../../../components/Form/FormForm';
import { t } from '../../../../locales/translate';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { handleError } from '../../../../model/notification';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { VOUCHER_CODES } from './voucher_codes_list';

const VOUCHER_CODE = graphql(`
  query VoucherCode($id: ID!) {
    voucherCode(id: $id) {
      code
      id
    }
  }
`);

const UPDATE_VOUCHER_CODE = graphql(`
  mutation UpdateVoucherCode($id: ID!, $code: String!) {
    updateVoucherCode(input: { id: $id, code: $code }) {
      voucherCode {
        id
        code
      }
    }
  }
`);

interface Props {
  id: string;
}

export function VoucherCodeUpdate({ id }: Props): JSX.Element {
  const { data } = useSuspenseQuery(VOUCHER_CODE, { variables: { id } });
  const { update: translation } = t.page.voucherCampaign.voucherCodes;
  const { open, Modal, size, close } = useModal();

  const [update] = useMutation(UPDATE_VOUCHER_CODE, {
    // In theory, VOUCHER_CODES should get the new value from the cache after we update the VoucherCode.
    // In practice, this only works around half the time. By forcing the refetch, it works all the time.
    refetchQueries: [VOUCHER_CODES],
  });

  const { fields, dirty, valid, onSubmit } = useForm({
    model: {
      code: data.voucherCode.code,
    },
    onSubmit: async ({ model }) => {
      if (!model.code) return;

      await update({
        variables: {
          id,
          code: model.code,
        },
      });

      close();
    },
    validations: {
      code: 'required',
    },
    onSubmitError: handleError,
  });

  return (
    <Modal open={open} title={translation.editVoucherCode} size={size}>
      <FormForm onSubmit={onSubmit} onClose={close} canSubmit={dirty && valid}>
        <FormTextField {...fields.code} label={translation.enterCode} name="code" required />
      </FormForm>
    </Modal>
  );
}
